import { resources } from "@constants/hardcoded-strings";
import { AxnReferenceDataTypes } from "@enums/axn-reference-data-types";
import { formatRpServiceConfiguration, unformatRpServiceConfiguration } from "@formatters/rp-services/rp-service-configuration-formatter";
import { LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnServiceOfferingDataSource
} from "@idw-types/data/reference-data/axn";
import {
    ApAssertion,
    Assertion,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    DeployedPolicyComponent,
    FallbackServiceMapping,
    RpServiceAttributeProperty,
    RpServiceAttributePropertyMap,
    ServiceOffering
} from "@idw-types/data/reference-data/generic";
import {
    FormattedRpServiceConfiguration,
    RpServiceConfiguration
} from "@idw-types/data/rp-service-configuration";
import { setVerificationServiceDetails } from "@redux/actions/verification-service-details-actions";
import { updateVerificationServiceDetailsRequest } from "@requests/service-configuration";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { Dispatch } from "redux";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { ApAssertionStatusMap } from "@idw-types/data/ap-assertion-status-map/ap-assertion-status-map";

export const updateVerificationServiceDetailsReduxRequest = (dispatch: Dispatch) => async (
    verificationServiceDetails: FormattedRpServiceConfiguration,
    identityProviders: Array<AxnIdentityProvider>,
    relyingParties: Array<AxnRelyingParty>,
    availablePropertyCategories: Array<AxnAvailablePropertyCategory>,
    serviceOfferings: Array<ServiceOffering>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>,
    attributeTypes: Array<AttributeType>,
    attributeLabels: Array<AttributeLabel>,
    attributeClassifications: Array<AttributeClassification>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    deployedPolicyComponents: Array<DeployedPolicyComponent>,
    axnAttributeTypeFields: Array<AxnAttributeTypeField>,
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>,
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>,
    assertionStatusMaps: Array<ApAssertionStatusMap>,
    assertionScoreServiceOfferingIDs: Array<string>,
    trustScoreServiceOfferingIDs: Array<string>,
    axnCompanies: Array<AxnCompany>,
    availableLanguages: Array<LocaleTypes>
): Promise<FormattedRpServiceConfiguration> => {

    const finalRpServiceConfiguration: RpServiceConfiguration = unformatRpServiceConfiguration(verificationServiceDetails, availableLanguages);
    const { data, error, body } = await updateVerificationServiceDetailsRequest(verificationServiceDetails.id, finalRpServiceConfiguration);
    if (data?.transferObject) {
        await getCommonReferenceDataReduxRequest(dispatch)(
            [AxnReferenceDataTypes.SIMPLE_AXN_WORKFLOW, AxnReferenceDataTypes.SIMPLE_AXN_ADMIN_WORKFLOW],
            ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
            null
        );
        const formattedRpServiceConfiguration = formatRpServiceConfiguration(
            data.transferObject,
            identityProviders,
            relyingParties,
            availablePropertyCategories,
            serviceOfferings,
            serviceOfferingDataSources,
            fallbackServiceMappings,
            attributeTypes,
            attributeLabels,
            attributeClassifications,
            apAssertions,
            assertions,
            deployedPolicyComponents,
            axnAttributeTypeFields,
            rpServiceAttributeProperties,
            rpServiceAttributePropertyMaps,
            assertionStatusMaps,
            assertionScoreServiceOfferingIDs,
            trustScoreServiceOfferingIDs,
            axnCompanies
        );
        // todo - the permissions returned from the update request are all false. Needs to be fixed by backend
        // the workflow ID is apparently null as well
        // configurationSettings also not coming back
        dispatch(setVerificationServiceDetails({
            ...formattedRpServiceConfiguration,
            permissions: verificationServiceDetails.permissions,
            workflowID: formattedRpServiceConfiguration.workflowID,
            workflow: formattedRpServiceConfiguration.workflow
        }));
        displaySuccessModal(
            dispatch,
            resources.requests.updateVerificationServiceDetails.success.title,
            resources.requests.updateVerificationServiceDetails.success.message
        );
        return formattedRpServiceConfiguration;
    } else {
        if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.updateVerificationServiceDetails.error.title,
                resources.requests.updateVerificationServiceDetails.error.message,
                error,
                null,
                body
            );
        }
    }
    return null;
};